import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { BulkActionKind, BulkCustomActionConfig, BulkExportLevel } from 'types/BulkActionTypes';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

/*
 * BulkActionConfirmStep.tsx
 * The confirmation step of the bulk actions panel, mirrors the convention
 * of DeleteModal.tsx. For a custom action it fetches the conditional
 * warning before the user can confirm, for an export it lets the user
 * choose the export format and, when the list supports it, the export
 * level (one row per document or one row per line).
 */

interface BulkActionConfirmStepProps {
    objectKey: string;
    objectNameKey?: string;
    kind: BulkActionKind;
    selectedCount: number;
    customAction?: BulkCustomActionConfig;
    exportFormat: 'xlsx' | 'csv';
    onExportFormatChange: (format: 'xlsx' | 'csv') => void;
    exportLevel: BulkExportLevel;
    onExportLevelChange: (level: BulkExportLevel) => void;
    onConfirm: () => void;
    onCancel: () => void;
    confirmLoading: boolean;
}

const BulkActionConfirmStep: React.FC<BulkActionConfirmStepProps> = ({
    objectKey, objectNameKey, kind, selectedCount, customAction, exportFormat, onExportFormatChange,
    exportLevel, onExportLevelChange, onConfirm, onCancel, confirmLoading
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { handleLogout } = useAuthContext();
    const [warningKey, setWarningKey] = useState<string | null>(null);
    const [warningLoaded, setWarningLoaded] = useState<boolean>(false);
    const [lineExportAvailable, setLineExportAvailable] = useState<boolean>(false);

    // Resolve the singular and plural object name, used in both the title and the message
    const objectNameSingular = objectNameKey ? t(`${objectNameKey}.singular`) : '';
    const objectNamePlural = objectNameKey ? t(`${objectNameKey}.plural`) : '';
    const objectNameForCount = selectedCount === 1 ? objectNameSingular : objectNamePlural;

    // Format the selected count with locale aware thousand separators
    const formattedCount = selectedCount.toLocaleString(userLocale);

    // Fetch the conditional warning for a custom action before the user can confirm
    useEffect(() => {
        if (kind !== 'custom' || !customAction) {
            setWarningLoaded(true);
            return;
        }

        const fetchWarning = async () => {
            try {
                const responseData = await fetchData({
                    apiUrl: `get_bulk_custom_action_warning/${objectKey}/${customAction.key}`,
                    handleLogout
                });

                // Only show a warning when the backend actually returned one
                setWarningKey(responseData?.warning_key ?? null);
            } catch (error) {
                console.error('Error fetching bulk custom action warning', error);
            } finally {
                setWarningLoaded(true);
            }
        };

        fetchWarning();
    }, [kind, customAction, objectKey]);

    // Fetch whether the list supports a one row per line export, resets the level when it does not
    useEffect(() => {
        if (kind !== 'export') return;

        const fetchExportOptions = async () => {
            try {
                const responseData = await fetchData({
                    apiUrl: `get_bulk_export_options/${objectKey}`,
                    handleLogout
                });

                // Only offer the line level when the backend reports it as available
                const available = Boolean(responseData?.line_export_available);
                setLineExportAvailable(available);
                if (!available) onExportLevelChange('document');
            } catch (error) {
                console.error('Error fetching bulk export options', error);
            }
        };

        fetchExportOptions();
    }, [kind, objectKey]);

    // Determine the header of the confirm step based on the action kind, always using the plural object name
    const getHeader = () => {
        if (kind === 'delete') return t('bulk_actions.general.delete_confirm_title', { objectName: objectNamePlural });
        if (kind === 'deactivate') return t('bulk_actions.general.deactivate_confirm_title', { objectName: objectNamePlural });
        if (kind === 'restore') return t('bulk_actions.general.restore_confirm_title', { objectName: objectNamePlural });
        if (kind === 'export') return t('bulk_actions.general.export_confirm_title', { objectName: objectNamePlural });
        return customAction ? t(customAction.confirmTitle ?? customAction.label, { objectName: objectNamePlural }) : '';
    };

    // Determine the body message of the confirm step based on the action kind
    const getMessage = () => {
        if (kind === 'delete') return t('bulk_actions.general.delete_confirm_message', { count: selectedCount, formattedCount, objectName: objectNameForCount });
        if (kind === 'deactivate') return t('bulk_actions.general.deactivate_confirm_message', { count: selectedCount, formattedCount, objectName: objectNameForCount });
        if (kind === 'restore') return t('bulk_actions.general.restore_confirm_message', { count: selectedCount, formattedCount, objectName: objectNameForCount });
        if (kind === 'export') return t('bulk_actions.general.export_confirm_message', { count: selectedCount, formattedCount, objectName: objectNameForCount });
        return customAction ? t(customAction.confirmMessage ?? "", { count: selectedCount, formattedCount, objectName: objectNameForCount }) : '';
    };

    // Determine the confirm button label based on the action kind
    const getConfirmButtonLabel = () => {
        if (kind === 'delete') return 'forms.delete_button_label';
        if (kind === 'deactivate') return 'forms.deactivate_button_label';
        if (kind === 'restore') return 'general.revert';
        if (kind === 'export') return 'bulk_actions.general.export_confirm_button_label';
        return 'bulk_actions.general.confirm_button_label';
    };

    return (
        <div className='bulk-action-confirm-step'>
            <h4 className='header'>
                {getHeader()}
            </h4>
            <p className='selection-count-text'>
                {t('bulk_actions.general.selected_count_label', { count: selectedCount, formattedCount, objectName: objectNameForCount })}
            </p>
            <p className='confirm-message'>
                {getMessage()}
            </p>
            {kind === 'export' && (
                // Let the user choose the export format before confirming
                <div className='export-format-choice'>
                    <label className={exportFormat === 'xlsx' ? 'active' : ''}>
                        <input
                            type='radio'
                            name='export-format'
                            checked={exportFormat === 'xlsx'}
                            onChange={() => onExportFormatChange('xlsx')}
                        />
                        {t('bulk_actions.general.export_format_xlsx_label')}
                    </label>
                    <label className={exportFormat === 'csv' ? 'active' : ''}>
                        <input
                            type='radio'
                            name='export-format'
                            checked={exportFormat === 'csv'}
                            onChange={() => onExportFormatChange('csv')}
                        />
                        {t('bulk_actions.general.export_format_csv_label')}
                    </label>
                </div>
            )}
            {kind === 'export' && lineExportAvailable && (
                // Let the user choose between one row per document and one row per line
                <div className='export-level-choice'>
                    <p className='export-choice-label'>{t('bulk_actions.general.export_level_label')}</p>
                    <label className={exportLevel === 'document' ? 'active' : ''}>
                        <input
                            type='radio'
                            name='export-level'
                            checked={exportLevel === 'document'}
                            onChange={() => onExportLevelChange('document')}
                        />
                        {t('bulk_actions.general.export_level_document_label', { objectName: objectNameSingular })}
                    </label>
                    <label className={exportLevel === 'line' ? 'active' : ''}>
                        <input
                            type='radio'
                            name='export-level'
                            checked={exportLevel === 'line'}
                            onChange={() => onExportLevelChange('line')}
                        />
                        {t('bulk_actions.general.export_level_line_label')}
                    </label>
                </div>
            )}
            {kind === 'custom' && warningKey && (
                // Show the conditional warning returned by the backend
                <div className='bulk-action-warning'>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <p>{t(warningKey)}</p>
                </div>
            )}
            <div className='button-row'>
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={onCancel}
                        label='bulk_actions.general.back_button_label'
                        size='small'
                    />
                    <PrimaryButton
                        onClick={onConfirm}
                        label={getConfirmButtonLabel()}
                        size='small'
                        loading={confirmLoading || (kind === 'custom' && !warningLoaded)}
                    />
                </div>
            </div>
        </div>
    );
};

export default BulkActionConfirmStep;
